import {
  MembershipBillingCycle,
  MembershipFeatureRowId,
  MembershipPlan,
  MembershipPlanPriceView,
  MembershipPlanRelation,
} from "@/features/pathpick/features/membership/types";
import { PathPickBillingCycle } from "@/features/pathpick/features/membership/types/request-body";

// 멤버십 화면 전용 팔레트 — 시안(프로토타입 멤버십 페이지) 실측값.
// 앱 공통 팔레트와 다른 이 화면만의 시스템이라 여기 가둬 쓴다.
export const MEMBERSHIP_COLORS = {
  textStrong: "#172033", // 제목·값
  textMuted: "#68758A", // 설명
  textFaint: "#8792A4", // 라벨·취소선 정가
  blue: "#2568C9", // 프라이머리 버튼·선택 토글·아이콘 배경
  blueDeep: "#174F99", // 가격·배지 텍스트·보조 버튼 텍스트
  blueBg: "#EDF5FF", // 배지·안내 배너 배경
  blueBorder: "#9AB8DF", // 안내 배너·보조 버튼 보더
  cardBg: "#FCFDFF",
  cardBorder: "#DCE3EC",
  amber: "#8A6222", // "/ 월"·첫 달 할인 뱃지
  promoText: "#B42318",
  promoBg: "#FFF1F0",
  promoBorder: "#FECDCA",
  toggleTrackBg: "#E9EEF5",
  chipOnBlue: "#DCEAFF", // 선택된 토글 안의 할인 문구
} as const;
export const MEMBERSHIP_CARD_SHADOW = "0px 12px 30px rgba(30, 54, 88, 0.06)";
export const MEMBERSHIP_BLUE_SHADOW = "0px 6px 16px rgba(37, 104, 201, 0.22)";

// 연 결제 정가 할인율 — (월가×12 − 연가) / (월가×12) = 16.7% → 17
export const YEARLY_DISCOUNT_RATE_LABEL = 17;
// 첫 달 50% 할인이 얹힌 연 결제 실질 할인율 — (월가×12 − 연 프로모션가) / (월가×12) = 20.8% → 21
export const PROMO_YEARLY_DISCOUNT_RATE_LABEL = 21;
// 오픈 기념 첫 달 할인율
export const FIRST_MONTH_DISCOUNT_RATE = 50;

// TODO: 멤버십 플랜 API 연결 시 교체
export const MEMBERSHIP_PLANS: MembershipPlan[] = [
  {
    id: "basic",
    englishName: "BASIC",
    order: 0,
    monthlyPrice: 300_000,
    yearlyPrice: 3_000_000,
    featureValueKeys: {
      product_limit: "plan_card.value.monthly_1000_products",
      api_sync: "plan_card.value.smartstore_sync",
      product_file: "plan_card.value.smartstore_kream",
      hot_deal: "plan_card.value.hot_deal_list",
      hot_deal_limit: "plan_card.value.hot_deal_limit_5m",
    },
  },
  {
    id: "pro",
    englishName: "PRO",
    order: 1,
    monthlyPrice: 600_000,
    yearlyPrice: 6_000_000,
    featureValueKeys: {
      product_limit: "plan_card.value.unlimited",
      api_sync: "plan_card.value.smartstore_sync",
      product_file: "plan_card.value.smartstore_kream",
      hot_deal: "plan_card.value.hot_deal_list",
      hot_deal_limit: "plan_card.value.hot_deal_limit_20m",
    },
  },
  {
    id: "enterprise",
    englishName: "ENTERPRISE",
    order: 2,
    monthlyPrice: null,
    yearlyPrice: null,
    featureValueKeys: {
      product_limit: "plan_card.value.unlimited",
      api_sync: "plan_card.value.smartstore_sync",
      product_file: "plan_card.value.smartstore_kream",
      hot_deal: "plan_card.value.hot_deal_list",
      hot_deal_limit: "plan_card.value.negotiable",
      // 오더액 기준 수수료는 enterprise에만 있는 행이다.
      order_fee: "plan_card.value.negotiable",
    },
  },
];

// 플랜 카드 제공 항목 행 — 렌더 순서 고정, 월 핫딜 한도 행에만 안내 모달 트리거 노출
export const MEMBERSHIP_FEATURE_ROWS: {
  id: MembershipFeatureRowId;
  labelKey: string;
  hasInfoModal?: boolean;
}[] = [
  { id: "product_limit", labelKey: "plan_card.feature.product_limit" },
  { id: "api_sync", labelKey: "plan_card.feature.api_sync" },
  { id: "product_file", labelKey: "plan_card.feature.product_file" },
  { id: "hot_deal", labelKey: "plan_card.feature.hot_deal" },
  {
    id: "hot_deal_limit",
    labelKey: "plan_card.feature.hot_deal_limit",
    hasInfoModal: true,
  },
  { id: "order_fee", labelKey: "plan_card.feature.order_fee" },
];

export const getPlanRelation = (
  plan: MembershipPlan,
  currentPlan: MembershipPlan | undefined
): MembershipPlanRelation => {
  if (!currentPlan) {
    return "none";
  }
  if (plan.order === currentPlan.order) {
    return "current";
  }
  return plan.order > currentPlan.order ? "upgrade" : "downgrade";
};

// 신청 내역의 plan.code("BASIC")와 화면 플랜의 englishName을 맞춘다.
// code에 enum 스펙이 없어 대소문자를 무시한다(findPlanIdByCode와 같은 규칙).
export const matchesPlanCode = (
  plan: MembershipPlan,
  code: string | null | undefined
) => code != null && plan.englishName.toUpperCase() === code.toUpperCase();

// 첫 달 50% 할인이 적용된 청구액.
// 월 결제는 첫 달 금액이 곧 청구액이라 절반이 되고, 연 결제는 1년 정가에서 "월 정가의 절반"만 빠진다.
// (연 환산 월액이 아니라 월 정가 기준이다: 3,000,000 − 150,000 = 2,850,000)
export const getPromoPrice = (
  plan: MembershipPlan,
  billingCycle: MembershipBillingCycle
): number | null => {
  if (plan.monthlyPrice === null) return null;

  const firstMonthDiscount = Math.round(
    (plan.monthlyPrice * FIRST_MONTH_DISCOUNT_RATE) / 100
  );

  if (billingCycle === "monthly") {
    return plan.monthlyPrice - firstMonthDiscount;
  }
  return plan.yearlyPrice !== null
    ? plan.yearlyPrice - firstMonthDiscount
    : null;
};

// 가격 블록에 필요한 값을 한 번에 고른다. 컴포넌트는 분기 없이 그리기만 하도록.
// enterprise처럼 가격이 없는 플랜은 null을 돌려주고 호출부가 상담 안내 박스로 대체한다.
export const selectMembershipPlanPriceView = (
  plan: MembershipPlan,
  billingCycle: MembershipBillingCycle,
  isFirstSubscription: boolean
): MembershipPlanPriceView | null => {
  const isYearly = billingCycle === "yearly";
  const listPrice = isYearly
    ? // 연 결제의 정가 기준은 연가가 아니라 "월 정가 12개월치"다 (3,600,000).
      plan.monthlyPrice !== null
      ? plan.monthlyPrice * 12
      : null
    : plan.monthlyPrice;

  if (listPrice === null) return null;

  const promoPrice = isFirstSubscription
    ? getPromoPrice(plan, billingCycle)
    : null;
  const regularPrice = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
  const chargedPrice = promoPrice ?? regularPrice;

  if (chargedPrice === null) return null;

  return {
    // 취소선 정가는 프로모션가와 대비될 때만 의미가 있다.
    // 기존 회원의 연 결제는 이미 "연간 결제 최대 17% 할인" 배지로 정가 대비를 표현한다.
    listPrice: promoPrice !== null ? listPrice : null,
    chargedPrice,
    monthlyEquivalent: isYearly ? Math.round(chargedPrice / 12) : null,
    yearlyDiscountRate: isYearly
      ? isFirstSubscription
        ? PROMO_YEARLY_DISCOUNT_RATE_LABEL
        : YEARLY_DISCOUNT_RATE_LABEL
      : null,
  };
};

// 패스픽 멤버십 입금 계좌.
// TODO: 입금 계좌 API/설정 연결 시 서버 값으로 대체
export const MEMBERSHIP_BANK_ACCOUNT = {
  bankName: "우리은행",
  accountNumber: "1005-804-502502",
  accountHolder: "패스레이드",
};

// 멤버십 신청 내역 쿼리 키 — 신청 직후 무효화해 입금 대기 판정이 바로 반영되게 한다.
export const PATHPICK_MEMBERSHIP_APPLICATIONS_QUERY_KEY = (
  companyId: number | undefined
) => ["pathpick", "membership-applications", companyId];

// 화면 결제 주기 → 신청 바디의 billing_cycle 값.
export const MEMBERSHIP_BILLING_CYCLE_BODY_VALUE: Record<
  MembershipBillingCycle,
  PathPickBillingCycle
> = {
  monthly: "MONTHLY",
  yearly: "YEARLY",
};
